/* eslint-disable */
import type { DateTime } from "../../../../common/time.pb";
import type { AppVersion } from "../../../../franklin/common.pb";
import type { UiCustomer } from "../../../../franklin/ui/customer.pb";
import type { UiPayment } from "../../../../franklin/ui/payment.pb";
import type { Paycheck } from "../../../paychecks/Paycheck.pb";
import type { DomainType } from "../../../syncentity/DomainType.pb";
import type { ActivityItemId, ActivityToken } from "./coremodels.pb";

/** Activity section enum type to be used on the read apis * */
export enum ActivityRowSection {
  DEFAULT_ACTIVITY_ROW_SECTION_DO_NOT_USE = "DEFAULT_ACTIVITY_ROW_SECTION_DO_NOT_USE",
  PENDING = "PENDING",
  UPCOMING = "UPCOMING",
  COMPLETED = "COMPLETED",
}

/** Source that is creating / updating the activity item. */
export enum ActivitySource {
  NEWSWRITER_BACKFILL = "NEWSWRITER_BACKFILL",
}

/** The set of fields required to uniquely identify an activity item. */
export interface ActivityItemGlobalId {
  /**
   * Required (Unless activity_row_id is provided). An activity item is uniquely identified with an activity_item_id
   * and a primary_activity_token.
   * The activity_item_id includes an id value and a type. Both are required.
   */
  activity_item_id?:
    | ActivityItemId
    | undefined;
  /**
   * Required (Unless activity_item_id is provided). Either one should be set but not both
   * The activity_row_id is the encoded string representation of activity_item_id which embeds the
   * type information in it
   * An activity item is uniquely identified with an activity_item_id
   * and a primary_activity_token.
   * The activity_item_id includes an id value and a type. Both are required.
   */
  activity_row_id?:
    | string
    | undefined;
  /**
   * Required. This is the primary ActivityToken this item belongs to. An
   * activity token is the identifier for a feed of activity items.
   * The primary_activity_token type is usually of CUSTOMER_TOKEN. This will
   * set the main activity feed as this record’s primary feed. The type will be
   * acl’d and most services should CUSTOMER_TOKEN as the primary activity
   * token type unless there’s a scenario that requires otherwise
   */
  primary_activity_token?: ActivityToken;
}

/** This ActivityRow is rendered using PaymentHistoryKt. */
export interface PaymentHistoryInputsRow {
  entity_id?: string;
  payment?: UiPayment;
  sender?: UiCustomer;
  recipient?: UiCustomer;
  domain_type?: DomainType;
}

/**
 * An [ActivityRow] is an envelope for generic elements displayed on the
 * activity screen in CashApp.
 */
export interface ActivityRow {
  /**
   * For data backed by clientsync, this will be set to null except on get requests where it will be
   * associated with the request.
   * Once clientsync data is migrated to activity this should never be null
   */
  activity_item_global_id?: ActivityItemGlobalId;
  /** String version of the activity item id. This unique per feed. */
  row_id?: string;
  /** Version of the activity item returned. */
  version?: number;
  /** Display date of the activity item. */
  display_date_epoch_ms?: number;
  /** Indicates whether an item is badged (seen) or not */
  is_badged?: boolean;
  activity_row_section?: ActivityRowSection;
  /**
   * Item level version owned by Activity Service.
   * This would be equal to version if badging is not being used for this item and (possibly) greater
   * if badging is used (item_version is bumped up everytime a badge is cleared but version would
   * only change when the product service updates it)
   * This version is to be used while calling Badging Service to clear badges.
   */
  item_version?: number;
  /** Reserve 1-9 for metadata. */
  payment_history_inputs_row?: PaymentHistoryInputsRow | undefined;
  paycheck_row?: PaycheckRow | undefined;
}

/** To be used to populate required headers for internal apis */
export interface RequestContext {
  app_version?: AppVersion;
  date_time?: DateTime;
}

/**
 * Using a message here instead of a type enum to be forward compatible in case there are section specific
 * properties that can arise.
 */
export interface PendingSection {
}

export interface UpcomingSection {
}

/** Wrapper proto, for future proofing, in case any activity specific config need to be added */
export interface PaycheckRow {
  paycheck?: Paycheck;
}

/**
 * Determines the section that the activity item should be in. This data model is to be exclusively
 * used in write apis. Read apis will have a different model.
 * Note that some sections an be computed and not explicitly defined such as upcoming. Any explicitly
 * defined section will take priority over any computed section. For example, if the section is set to
 * pending and the display date is set to the future, the item will be categorized as pending
 * (not upcoming)
 */
export interface ActivitySection {
  pending_section?:
    | PendingSection
    | undefined;
  /** 2 is used for expire_section_after */
  upcoming_section?:
    | UpcomingSection
    | undefined;
  /**
   * When this field is set, the item will no longer appear in the defined section after the time has
   * passed and will instead go back the main activity feed. If not explicitly set, then the item will
   * remain in the defined section indefinitely until another update occurs.
   *
   * GUIDANCE ON USAGE: This is useful for things like expiration where you do not want the record
   * to be removed but you only want it to no longer show up in the defined section. It's good to use
   * for scenarios. where things will deterministically happen just due to passage of time.
   *
   * This should NOT be used in the prediction of actual changing state. For example, if a payment
   * is expected to be processed at a certain time, the result of that process (whether success or
   * failure) should be directly communicated and the section explicitly changed by the product team
   * with a write. Otherwise we risk showing "successful" payments when they in fact failed. And if the
   * product team also fails to notify us, the customer will never know. This can really harm customer
   * trust.
   */
  expire_section_after?: number;
}
